import styles from './search.module.sass'

import instantsearch from 'instantsearch.js'
import algoliasearch from 'algoliasearch'

import { searchBox, configure, pagination, stats } from 'instantsearch.js/es/widgets'
import { connectHits } from 'instantsearch.js/es/connectors'

import { useEffect, useRef } from 'react'

import classNames from 'classnames'

import Title from './basic/title'
import Wysiwyg from './wysiwyg'

const searchClient = algoliasearch('S2ICXJZ8X9', 'b248fbbe179ca55339fb91328a226508');

function Search(props) {
	const searchWrapperRef = useRef(null)
	const searchContainerRef = useRef(null)
	const hitsContainerRef = useRef(null)
	const paginationContainerRef = useRef(null)
	const statsRef = useRef(null)
	const searchRef = useRef(null)

	useEffect(() => {
		if (props.editor) {
			return
		}

		const input = searchWrapperRef.current?.querySelector('input')

		if(input){
			input.addEventListener('focus', focusSearch)
			input.addEventListener('blur', blurSearch)
			
			return () => {
				input.removeEventListener('focus', focusSearch)
				input.removeEventListener('blur', blurSearch)
			}
		}
	}, [searchWrapperRef])

	useEffect(() => {
		if (props.editor) {
			return
		}

		const search = instantsearch({
			indexName: 'replica_date_desc',
			searchClient,
			searchFunction(helper) {
				// No filters - just search everything
				helper.state.filters = ''
				helper.search()
			}
		})
		searchRef.current = search

		const customHits = connectHits(
			(renderOptions) => {
				const { hits } = renderOptions
				let hitsHtml = ''

				hits.map((hit) => {
					hitsHtml += renderHit(hit)
				})

				renderOptions.widgetParams.container.innerHTML = hitsHtml
			}
		)

		search.addWidgets([
			configure({
				hitsPerPage: 12,
			}),
			pagination({
				container: paginationContainerRef.current,
				cssClasses: {
					selectedItem: styles.selected_page,
					firstPageItem: styles.first_page,
					previousPageItem: styles.previous_page,
					nextPageItem: styles.next_page,
					lastPageItem: styles.last_page,
					disabledItem: styles.disabled_pagination_item
				},
				showFirst: true,
				showLast: true,
				padding: 2,
				scrollTo: hitsContainerRef.current,
				templates: {
					previous: '',
					first: '«',
					last: '»',
					next: '',
				}
			}),
			searchBox({
				container: searchContainerRef.current
			}),
			customHits({
				container: hitsContainerRef.current
			}),
			stats({
				container: statsRef.current,
				templates: {
					text(data, { html }) {
						let totalResults = ''
						let currentResults = ''
						let currentLastResult = data.page * data.hitsPerPage + data.hitsPerPage
				  
						if (data.hasManyResults) {
						  totalResults += `${data.nbHits} results`
						} else if (data.hasOneResult) {
							totalResults += `1 result`
						} else {
							totalResults += `no results`
						}

						if(currentLastResult > data.nbHits){
							currentLastResult = data.nbHits
						}

						if(totalResults && data.nbHits > 1) {
							currentResults = `${data.page * data.hitsPerPage + 1} - ${currentLastResult}`
							return html `Viewing ${currentResults} of <span class="green">${totalResults}</span>`
						} else {
							return html `<span class="green">${totalResults}</span>`
						}
					},
				},
			})
		])

		search.start()

		initSearchQuery()

		return () => {
			// Cleanup if needed
		}
	}, [])

	function initSearchQuery() {
		const params = new Proxy(new URLSearchParams(window.location.search), {
			get: (searchParams, prop) => searchParams.get(prop),
		})
		if (params.query) {
			searchRef.current.helper.state.query = params.query
		}
	}

	function focusSearch(){
		searchWrapperRef.current.classList.add(styles.search_focused)
	}

	function blurSearch(){
		searchWrapperRef.current.classList.remove(styles.search_focused)
	}

	function renderHit(hit) {
		let hitsImageHtml = ''
		let hitWrapperOpen = ''
		let hitWrapperClose = ''
		let date = ''
		let title = hit.title
		let tagsHtml = ''

		// Determine date
		if (hit.type == 'event' || hit.type == 'presentation' || hit.type == 'new_event') {
			if(!hit.start_date){
				date = new Date(hit.published_gmt * 1000).toLocaleDateString('en-us', {timeZone:"UTC", year:"numeric", month:"long", day:"numeric"})
			} else if (hit.start_date == hit.end_date || hit.start_date && !hit.end_date || new Date(hit.start_date * 1000).toLocaleDateString('en-us', {timeZone:"UTC", year:"numeric", month:"long", day:"numeric"}) == new Date(hit.end_date * 1000).toLocaleDateString('en-us', {timeZone:"UTC", year:"numeric", month:"long", day:"numeric"})){
				date = new Date(hit.start_date * 1000).toLocaleDateString('en-us', {timeZone:"UTC", year:"numeric", month:"long", day:"numeric"})
			} else {
				date = `${new Date(hit.start_date * 1000).toLocaleDateString('en-us', {timeZone:"UTC", month:"long", day:"numeric"})} - ${new Date(hit.end_date * 1000).toLocaleDateString('en-us', {timeZone:"UTC", year:"numeric", month:"long", day:"numeric"})}`
			}
		} else {
			date = new Date(hit.published_gmt * 1000).toLocaleDateString('en-us', {timeZone:"UTC", year:"numeric", month:"long", day:"numeric"})
		}

		// Determine category label
		function category(hit) {
			if (hit.type == 'news') return 'News'
			if (hit.type == 'blog') return 'Blog'
			if (hit.type == 'page') return 'Page'
			if (hit.type == 'new_event') return 'Event'
			if (hit.type == 'presentation') return hit.category || 'Event'
			if (hit.category) return hit.category
			return ''
		}

		// Build image HTML
		if (hit.type == 'asset' || hit.type == 'data') {
			hitsImageHtml = `<img src="/wp-content/themes/personalis/assets/resources/${hit.category}.svg" alt="${hit.category}" class="${styles.placeholder}" />`
		} else if(hit.image && !hit.image.length && hit.type != 'presentation'){
			if(hit.image.sizes && hit.image.sizes.medium_large){
				hitsImageHtml = `<img src="${hit.image.sizes.medium_large}" width="${hit.image.sizes['medium_large-width'] || ''}" height="${hit.image.sizes['medium_large-height'] || ''}" />`
			}
		} else if(hit.image && hit.image.length > 0 && hit.type != 'presentation'){
			hit.image.map((image, i) => {
				if (image && image.sizes && image.sizes.medium_large) {
					hitsImageHtml += `<img src="${image.sizes.medium_large}" width="${image.sizes['medium_large-width'] || ''}" height="${image.sizes['medium_large-height'] || ''}" />`
				}
			})
		}

		// Build tags
		if(hit.type == 'event' || hit.type == 'presentation'){
			if(hit.conference) tagsHtml += `<span class="${styles.tag}">${hit.conference}</span>`
			if(hit.start_date) tagsHtml += `<span class="${styles.tag}">${new Date(hit.start_date * 1000).toLocaleDateString('en-us', {timeZone:"UTC", year:"numeric"})}</span>`
			if(hit.category) tagsHtml += `<span class="${styles.tag}">${hit.category}</span>`
		}

		// Build wrapper
		hitWrapperOpen = `<a id="${hit.objectID}" class="${styles.hit} ${styles[hit.type] || ''} ${hit.classes || ''}" href="${hit.download_path || hit.path}" target="${hit.download_path ? '_blank' : hit.target || '_self'}">`
		hitWrapperClose = '</a>'

		return `${hitWrapperOpen}
			${hitsImageHtml ? `<div class="${styles.image_wrapper}">
				${hitsImageHtml}
			</div>` : ''}
			<div class="${styles.hit_content}">
				<div class="${styles.hit_header}">
					<span class="${styles.date}">${date}</span>
					${category(hit) ? ` | <span class="${styles.category}">${category(hit)}</span>` : ''}
				</div>
				<div class="${styles.description}">
					<h3>${title}</h3>
					${hit.subtitle ? `<h4>${hit.subtitle}</h4>` : ''}
					${hit.excerpt || hit.meta_description ? `<p>${hit.excerpt || hit.meta_description}</p>` : ''}
				</div>
				${tagsHtml ? `<div class="${styles.tags_wrapper}">${tagsHtml}</div>` : ''}
				<div class="${styles.button_group}">
					<span class="${styles.read_more}">Read More</span>
				</div>
			</div>
		${hitWrapperClose}`
	}

	return props.editor ? <div>Search Component</div> : <div ref={searchWrapperRef} className={styles.search_wrapper}>

		<Title fields={{heading_type: 'h1', title: props.fields.title, subtitle: props.fields.subtitle}} />
		{props.fields.description && <Wysiwyg className={styles.intro} fields={{content: props.fields.description}} />}

		<div className={styles.search_bar_wrapper}>
			<div ref={searchContainerRef} className={styles.search_container}>
				<h2><span>Search:</span></h2>
			</div>
		</div>

		<div ref={statsRef} className={styles.stats}></div>

		<div ref={hitsContainerRef} className={`${styles.results} ${styles.list_layout}`}></div>

		<div className={styles.pagination_container} ref={paginationContainerRef}>
		</div>
	</div>
}

export default Search